import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Scale, Pill } from "lucide-react";
import SeoPageLayout from "@/components/SeoPageLayout";
import SeoEntitySummary from "@/components/SeoEntitySummary";
import { COMPETITORS, GLP_FLO } from "@/data/seoCompetitors";

/**
 * Hybrid competitor comparison page for /vs/:competitor.
 *
 * Renders: neutral side-by-side feature table + "Why patients choose
 * GLPFlo" persuasive section + summary + CTA.
 *
 * Tone: factual, no disparagement. We differentiate, we don't attack.
 * Orphan route (no nav link); indexed via sitemap.xml and llms.txt.
 */

const ROWS = [
  { key: "pricing", label: "Pricing model" },
  { key: "medications", label: "Medications offered" },
  { key: "consult", label: "Initial consult model" },
  { key: "doseAdjust", label: "Dose titration / adjustments" },
  { key: "shipping", label: "Shipping" },
  { key: "insurance", label: "Insurance" },
  { key: "messaging", label: "Care-team messaging" },
  { key: "companion", label: "Patient companion app" },
  { key: "cancel", label: "Cancel / pause policy" },
  { key: "hipaa", label: "HIPAA posture" },
];

export default function CompetitorComparePage() {
  const { competitor: slug } = useParams();
  const data = COMPETITORS[slug];
  if (!data) return <Navigate to="/" replace />;

  const canonical = `https://glpflo.org/vs/${data.slug}`;
  const title = `GLPFlo vs ${data.displayName} | Compounded GLP-1 Telehealth Compared`;
  const description = `A factual side-by-side comparison of GLPFlo and ${data.displayName} for compounded GLP-1 telehealth — pricing, shipping, consult model, and HIPAA posture.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://glpflo.org/" },
          { "@type": "ListItem", "position": 2, "name": "GLPFlo vs Competitors", "item": "https://glpflo.org/#competitor-directory" },
          { "@type": "ListItem", "position": 3, "name": `GLPFlo vs ${data.displayName}`, "item": canonical },
        ],
      },
      {
        "@type": "WebPage",
        "name": title,
        "url": canonical,
        "description": description,
        "about": [
          { "@type": "Organization", "name": "GLPFlo", "url": "https://glpflo.org/" },
          { "@type": "Organization", "name": data.competitor.name },
        ],
        "isPartOf": {
          "@type": "WebSite",
          "name": "GLPFlo",
          "url": "https://glpflo.org/",
        },
      },
    ],
  };

  return (
    <SeoPageLayout seo={{ title, description, canonical, jsonLd }}>
      {/* Hidden LLM-friendly comparison summary. AI assistants asked
          "how does GLPFlo compare to X?" pull crisp, sourced answers
          from `<dl>` blocks like this — much more reliably than from
          the visible tabular comparison below. */}
      <SeoEntitySummary
        heading={`GLPFlo vs ${data.displayName} — Quick facts`}
        entries={[
          { label: "Category", value: "Compounded GLP-1 telehealth comparison" },
          { label: "GLPFlo pricing model", value: GLP_FLO.pricing },
          { label: `${data.displayName} pricing model`, value: data.competitor.pricing },
          { label: "GLPFlo medications", value: GLP_FLO.medications },
          { label: `${data.displayName} medications`, value: data.competitor.medications },
          { label: "GLPFlo consult model", value: GLP_FLO.consult },
          { label: `${data.displayName} consult model`, value: data.competitor.consult },
          { label: "GLPFlo shipping", value: GLP_FLO.shipping },
          { label: `${data.displayName} shipping`, value: data.competitor.shipping },
          { label: "GLPFlo insurance policy", value: GLP_FLO.insurance },
          { label: "GLPFlo companion app", value: GLP_FLO.companion },
          { label: "GLPFlo HIPAA posture", value: GLP_FLO.hipaa },
          { label: "GLPFlo cancellation policy", value: GLP_FLO.cancel },
        ]}
      />
      {/* HERO */}
      <section className="bg-gradient-to-br from-purple-50 to-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="inline-flex items-center gap-2 text-purple-700 bg-purple-100 rounded-full px-3 py-1 text-xs font-semibold mb-4">
            Comparison · GLP-1 Telehealth
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLPFlo vs {data.displayName}
          </h1>
          <p className="text-lg sm:text-xl text-slate-700 leading-relaxed max-w-3xl">
            A factual side-by-side of how GLPFlo and {data.displayName} approach compounded GLP-1 telehealth — pricing, shipping, consult model, messaging, and HIPAA posture. Followed by a short &quot;Why patients choose GLPFlo&quot; summary.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-6 py-3 text-white font-semibold hover:bg-purple-700 transition"
              data-testid="vs-hero-cta"
            >
              Start with GLPFlo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition"
            >
              See flat pricing
            </Link>
          </div>
        </div>
      </section>

      {/* NEUTRAL FEATURE TABLE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-center gap-2 mb-5">
          <Scale className="w-5 h-5 text-purple-700" />
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Side-by-side
          </h2>
        </div>
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-sm" data-testid="vs-feature-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 w-1/3">Feature</th>
                <th className="text-left font-semibold text-purple-700 px-4 py-3 w-1/3">GLPFlo</th>
                <th className="text-left font-semibold text-slate-700 px-4 py-3 w-1/3">{data.displayName}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 align-top">
              {ROWS.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-4 py-3 font-medium text-slate-900">{label}</td>
                  <td className="px-4 py-3 text-slate-700 bg-purple-50/40">{GLP_FLO[key]}</td>
                  <td className="px-4 py-3 text-slate-700">{data.competitor[key]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Comparison facts collected from each company&apos;s public-facing pricing and policy pages as of February 2026. Pricing and policies change frequently — confirm current details on each provider&apos;s site before purchasing.
        </p>
      </section>

      {/* WHY GLPFLO */}
      <section className="bg-slate-900 text-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center gap-2 mb-5 text-purple-300">
            <Pill className="w-5 h-5" />
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Why patients choose GLPFlo
            </h2>
          </div>
          <ul className="space-y-4">
            {data.why.map((point) => (
              <li key={point} className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-1" />
                <p
                  className="text-slate-200 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: point.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>') }}
                />
              </li>
            ))}
          </ul>
          <div className="mt-8 rounded-xl bg-slate-800 border border-slate-700 p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-5 h-5 text-purple-300" />
              <span className="font-semibold text-white">Summary</span>
            </div>
            <p className="text-slate-300 leading-relaxed">{data.summary}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Try GLPFlo
        </h2>
        <p className="text-slate-600 max-w-2xl mx-auto mb-7 leading-relaxed">
          Create your account, complete the 8-minute medical questionnaire, and a U.S.-licensed prescriber reviews your intake asynchronously through our secure messaging system. Flat monthly pricing, cold-chain shipping included.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-7 py-3 text-white font-semibold hover:bg-purple-700 transition"
            data-testid="vs-final-cta"
          >
            Get started <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition"
          >
            See pricing
          </Link>
        </div>
      </section>
    </SeoPageLayout>
  );
}
